import { useMemo } from 'react';
import { Company } from 'api/models';
import { Task } from 'data/tasks';
import { useAccount } from 'hooks/use-account';
import { useTranslation } from 'hooks/use-translation';

export enum ProjectPlanFilterTypes {
  All = 'all',
  IncompleteTasks = 'tasks:incomplete',
  CompletedTasks = 'tasks:complete',
  Responsibility = 'responsible',
}
export interface ProjectPlanFilterOption {
  id: ProjectPlanFilterTypes;
  label: string;
  value: ProjectPlanFilterTypes | number;
  companyLogoUrl?: string;
  filter(
    task?: Pick<Task, 'responsible' | 'completedAt'>,
    selected?: ProjectPlanFilterOption,
  ): boolean;
}
export type ProjectPlanFilter = {
  primary: ProjectPlanFilterOption;
  secondary: ProjectPlanFilterOption;
};
export type ProjectPlanFilters = {
  primary: ProjectPlanFilterOption[];
  secondary: ProjectPlanFilterOption[];
};
interface ProjectPlanFilterProps {
  customer: Company;
  partners: Company[];
}

export const useProjectPlanFilter = ({
  customer,
  partners,
}: ProjectPlanFilterProps): [ProjectPlanFilters, ProjectPlanFilter] => {
  const { company } = useAccount();
  const allText = useTranslation('all');
  const allTasksText = useTranslation('all_tasks');
  const incompleteTasksText = useTranslation(
    'projects.filters.incomplete_tasks',
  );
  const completedTasksText = useTranslation('projects.filters.completed_tasks');

  const secondaryFilters: ProjectPlanFilterOption[] = useMemo(
    () =>
      [
        {
          label: allText,
          value: ProjectPlanFilterTypes.All,
          id: ProjectPlanFilterTypes.All,
          filter() {
            return true;
          },
        },
        {
          id: ProjectPlanFilterTypes.Responsibility,
          label: company.companyName,
          value: company.companyId,
          companyLogoUrl: company.companyLogoUrl,
          filter(task: Task, selected: ProjectPlanFilterOption) {
            return (task.responsible ?? [])
              .map((owner: Company) => owner.companyId)
              .includes(selected.value as number);
          },
        },
        ...(customer != null
          ? [
              {
                id: ProjectPlanFilterTypes.Responsibility,
                label: customer?.companyName,
                value: customer?.companyId,
                companyLogoUrl: customer?.companyLogoUrl,
                filter(task: Task, selected: ProjectPlanFilterOption) {
                  return (task.responsible ?? [])
                    .map((owner: Company) => owner.companyId)
                    .includes(selected.value as number);
                },
              },
            ]
          : []),
      ].concat(
        partners.map((partner) => ({
          id: ProjectPlanFilterTypes.Responsibility,
          label: partner.companyName,
          value: partner.companyId,
          companyLogoUrl: partner.companyLogoUrl,
          filter(task: Task, selected: ProjectPlanFilterOption) {
            return (task.responsible ?? [])
              .map((owner: Company) => owner.companyId)
              .includes(selected.value as number);
          },
        })),
      ),
    [
      allText,
      company.companyId,
      company.companyLogoUrl,
      company.companyName,
      customer,
      partners,
    ],
  );

  const filters: ProjectPlanFilters = useMemo(
    () => ({
      primary: [
        {
          label: allTasksText,
          id: ProjectPlanFilterTypes.All,
          value: ProjectPlanFilterTypes.All,
          filter() {
            return true;
          },
        },
        {
          label: incompleteTasksText,
          value: ProjectPlanFilterTypes.IncompleteTasks,
          id: ProjectPlanFilterTypes.IncompleteTasks,
          filter(task: Task) {
            return !task.completedAt;
          },
        },
        {
          label: completedTasksText,
          value: ProjectPlanFilterTypes.CompletedTasks,
          id: ProjectPlanFilterTypes.CompletedTasks,
          filter(task: Task) {
            return !!task.completedAt;
          },
        },
      ],
      secondary: secondaryFilters,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [secondaryFilters],
  );

  const defaultFilter = useMemo(
    () => ({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      primary: filters.primary.find(
        (filter) => filter.id === ProjectPlanFilterTypes.All,
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      secondary: filters.secondary.find(
        (filter) => filter.id === ProjectPlanFilterTypes.All,
      )!,
    }),
    [filters],
  );

  return [filters, defaultFilter];
};
